import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null);
    const [allowedModules, setAllowedModules] = useState([]);
    const [isImplicitAdmin, setIsImplicitAdmin] = useState(false);
    const [tenants, setTenants] = useState([]);
    const [currentTenant, setCurrentTenant] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            setLoading(true);

            const token = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');

            if (!token) {
                if (!cancelled) setLoading(false);
                return;
            }

            if (storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    if (!cancelled) setUser(parsedUser);
                } catch (error) {
                    console.error('Error parsing user data:', error);
                    localStorage.clear();
                    if (!cancelled) setLoading(false);
                    return;
                }
            }

            try {
                await loadUserData();
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        init();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadUserData = async () => {
        try {
            const response = await api.get('/me');
            setUser(response.data.user);
            setRole(response.data.role || null);
            setAllowedModules(response.data.allowed_modules || []);
            setIsImplicitAdmin(!!response.data.is_implicit_admin);
            setTenants(response.data.tenants);

            const storedTenantId = localStorage.getItem('tenant_id');
            if (storedTenantId) {
                const tenant = response.data.tenants.find(t => t.id === parseInt(storedTenantId));
                setCurrentTenant(tenant);
            } else if (response.data.tenants.length > 0) {
                setCurrentTenant(response.data.tenants[0]);
                localStorage.setItem('tenant_id', response.data.tenants[0].id);
            }
        } catch (error) {
            console.error('Error loading user data:', error);
        }
    };

    const login = async (email, password) => {
        try {
            setLoading(true);
            const response = await api.post('/login', { email, password });
            const { user, tenants, token } = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            setUser(user);
            setTenants(tenants);

            if (tenants.length > 0) {
                setCurrentTenant(tenants[0]);
                localStorage.setItem('tenant_id', tenants[0].id);
            }

            await loadUserData();

            return response.data;
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await api.post('/logout');
        } catch (error) {
            console.error('Error logging out:', error);
        } finally {
            localStorage.clear();
            setUser(null);
            setRole(null);
            setAllowedModules([]);
            setIsImplicitAdmin(false);
            setTenants([]);
            setCurrentTenant(null);
        }
    };

    const switchTenant = (tenant) => {
        setCurrentTenant(tenant);
        localStorage.setItem('tenant_id', tenant.id);
        window.location.reload(); // Reload to fetch new tenant data
    };

    const value = {
        user,
        role,
        allowedModules,
        isImplicitAdmin,
        can: (module) => isImplicitAdmin || allowedModules.includes('*') || allowedModules.includes(module),
        tenants,
        currentTenant,
        loading,
        login,
        logout,
        switchTenant,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

